import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { apiClient } from '../lib/api';
import Header from '../components/Header';
import MessageDialog from '../components/MessageDialog';
import { useMessageDialog } from '../hooks/useMessageDialog';
import { ReceivedFileMetaData, ReceivedFilesListResponse, Pagination, DownloadAllResponse, SingleFileDownloadResponse, MultipleFilesDownloadResponse } from '@df-gate-share/shared';
import JSZip from 'jszip';
import { saveAs } from 'file-saver';

type StatusFilter = 'all' | 'active' | 'expired';
type SortOrder = 'desc' | 'asc';

const ReceivedFiles: React.FC = () => {
  const navigate = useNavigate();
  const { dialogState, closeDialog, showAlert } = useMessageDialog();
  const [files, setFiles] = useState<ReceivedFileMetaData[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all');
  const [sortOrder, setSortOrder] = useState<SortOrder>('desc');
  const [pagination, setPagination] = useState<Pagination>({
    hasMore: false,
    count: 0
  });
  const [currentCursor, setCurrentCursor] = useState<string | undefined>(undefined);
  const [cursorHistory, setCursorHistory] = useState<(string | undefined)[]>([]);
  const [downloadingShareId, setDownloadingShareId] = useState<string | null>(null);

  useEffect(() => {
    fetchReceivedFiles();
  }, [statusFilter, sortOrder]);

  const fetchReceivedFiles = async (cursor?: string, resetHistory: boolean = false) => {
    setLoading(true);
    setError('');

    try {
      const params = new URLSearchParams();
      if (statusFilter !== 'all') {
        params.append('status', statusFilter);
      }
      params.append('sortOrder', sortOrder);
      if (cursor) params.append('cursor', cursor);
      params.append('limit', '50');

      const response = await apiClient.get(`/api/v1/received?${params.toString()}`);
      const respData = response.data as ReceivedFilesListResponse;
      setFiles(respData.data || []);
      setPagination(respData.pagination || { hasMore: false, count: 0 });

      if (resetHistory) {
        setCursorHistory([]);
      }
    } catch (err) {
      setError('エラーが発生しました。もう一度お試しください。');
    } finally {
      setLoading(false);
    }
  };

  const handleNextPage = () => {
    if (pagination.hasMore && pagination.nextCursor) {
      setCursorHistory([...cursorHistory, currentCursor]);
      setCurrentCursor(pagination.nextCursor);
      fetchReceivedFiles(pagination.nextCursor);
    }
  };

  const handlePrevPage = () => {
    if (cursorHistory.length > 0) {
      const newHistory = [...cursorHistory];
      const prevCursor = newHistory.pop();
      setCursorHistory(newHistory);
      setCurrentCursor(prevCursor);
      fetchReceivedFiles(prevCursor);
    }
  };

  const handleDownload = async (shareId: string, title?: string) => {
    setDownloadingShareId(shareId);
    try {
      const response = await apiClient.get(`/api/v1/shares/${shareId}/download-all`);
      const data = response.data as DownloadAllResponse;

      if (data.fileCount === 1) {
        // Single file - direct download
        window.open((data as SingleFileDownloadResponse).downloadUrl, '_blank');
      } else {
        // Multiple files - create zip archive
        const multiData = data as MultipleFilesDownloadResponse;
        if (multiData.files.length === 0) {
          showAlert('error', 'エラー', 'ダウンロード可能なファイルがありません');
          return;
        }

        const zip = new JSZip();

        for (const file of multiData.files) {
          try {
            const fileResponse = await fetch(file.downloadUrl);
            if (!fileResponse.ok) {
              throw new Error(`Failed to download ${file.fileName}`);
            }
            const blob = await fileResponse.blob();
            zip.file(file.fileName, blob);
          } catch (err) {
            console.error(`Error downloading ${file.fileName}:`, err);
            showAlert('error', 'エラー', `${file.fileName} のダウンロードに失敗しました`);
            return;
          }
        }

        const zipBlob = await zip.generateAsync({ type: 'blob' });
        const zipFileName = title ? `${title}.zip` : 'files.zip';
        saveAs(zipBlob, zipFileName);
      }
    } catch (err) {
      console.error('Download error:', err);
      showAlert('error', 'エラー', 'ダウンロードに失敗しました。');
    } finally {
      setDownloadingShareId(null);
    }
  };

  const formatFileSize = (bytes: number): string => {
    if (bytes === 0) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
  };

  const formatDate = (dateString: string): string => {
    const date = new Date(dateString);
    return date.toLocaleDateString('ja-JP', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const calculateDaysRemaining = (expiresAt: string): { days: number; text: string; isExpired: boolean; className: string } => {
    const now = new Date();
    const expiry = new Date(expiresAt);
    const diffTime = expiry.getTime() - now.getTime();
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

    if (diffDays < 0) {
      return { days: diffDays, text: '期限切れ', isExpired: true, className: 'expired' };
    } else if (diffDays <= 3) {
      return { days: diffDays, text: `あと${diffDays}日`, isExpired: false, className: 'warning' };
    } else {
      return { days: diffDays, text: `あと${diffDays}日`, isExpired: false, className: 'ok' };
    }
  };

  const getSenderInitial = (name?: string, email?: string): string => {
    if (name) return name.charAt(0);
    if (email) return email.charAt(0).toUpperCase();
    return '?';
  };

  return (
    <>
      <Header />
      <div className="page-wrapper">
        <div className="page-container">
          {/* Page Header */}
          <h1 className="page-title">
            受信ファイル
          </h1>

          {/* Error Message */}
          {error && (
            <div className="alert alert--error">
              {error}
            </div>
          )}

          {/* Filters */}
          <div className="filter-bar">
            <div className="filter-group">
              <label className="filter-label">
                ステータス:
              </label>
              <div className="filter-group">
                <button
                  onClick={() => setStatusFilter('all')}
                  className={`filter-btn${statusFilter === 'all' ? ' filter-btn--active' : ''}`}
                >
                  すべて
                </button>
                <button
                  onClick={() => setStatusFilter('active')}
                  className={`filter-btn${statusFilter === 'active' ? ' filter-btn--active' : ''}`}
                >
                  アクティブ
                </button>
                <button
                  onClick={() => setStatusFilter('expired')}
                  className={`filter-btn${statusFilter === 'expired' ? ' filter-btn--active' : ''}`}
                >
                  期限切れ
                </button>
              </div>
            </div>

            <div className="filter-group">
              <label className="filter-label">
                並び替え:
              </label>
              <select
                value={sortOrder}
                onChange={(e) => setSortOrder(e.target.value as SortOrder)}
                className="form-select"
              >
                <option value="desc">新しい順</option>
                <option value="asc">古い順</option>
              </select>
            </div>
          </div>

          {/* File List */}
          {loading ? (
            <div className="empty-state">
              読み込み中...
            </div>
          ) : !files || files.length === 0 ? (
            <div className="empty-state">
              <div className="empty-state__icon"><span className="material-symbols-outlined icon-xl">drafts</span></div>
              <div className="empty-state__title">
                受信ファイルがありません
              </div>
              <div className="empty-state__desc">
                他のユーザーから共有されたファイルがここに表示されます
              </div>
            </div>
          ) : (
            <>
              <div className="card">
                <div className="table-wrapper table-wrapper--responsive">
                  <table className="table">
                    <thead>
                      <tr>
                        <th>タイトル</th>
                        <th>ファイル</th>
                        <th>送信者</th>
                        <th>サイズ</th>
                        <th>受信日時</th>
                        <th>有効期限</th>
                        <th>ステータス</th>
                        <th>アクション</th>
                      </tr>
                    </thead>
                    <tbody>
                      {files.map((file) => {
                        const expiryInfo = calculateDaysRemaining(file.expiresAt);
                        const isInactive = file.status === 'expired' || file.status === 'revoked';

                        return (
                          <tr
                            key={file.shareId}
                            className={isInactive ? 'row--inactive' : ''}
                          >
                            {/* Title */}
                            <td data-label="タイトル">
                              <div style={{ fontWeight: 600, color: '#111827' }}>
                                {file.title}
                              </div>
                            </td>

                            {/* Files */}
                            <td data-label="ファイル">
                              {file.fileCount}ファイル
                            </td>

                            {/* Sender */}
                            <td data-label="送信者">
                              <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                <div className="avatar">
                                  {getSenderInitial(file.senderName, file.senderEmail)}
                                </div>
                                <div style={{ display: 'flex', flexDirection: 'column' }}>
                                  <div style={{ fontWeight: 500, color: '#111827', fontSize: '14px' }}>
                                    {file.senderName || file.senderEmail}
                                  </div>
                                  <div style={{ color: '#6b7280', fontSize: '12px' }}>
                                    {file.senderEmail}
                                  </div>
                                </div>
                              </div>
                            </td>

                            {/* Size */}
                            <td data-label="サイズ">
                              {formatFileSize(file.totalSize)}
                            </td>

                            {/* Received At */}
                            <td data-label="受信日時">
                              {formatDate(file.receivedAt)}
                            </td>

                            {/* Expiry */}
                            <td data-label="有効期限">
                              {formatDate(file.expiresAt)}
                            </td>

                            {/* Status */}
                            <td data-label="ステータス">
                              <span className={
                                file.status === 'active' ? 'badge badge--active' :
                                file.status === 'expired' ? 'badge badge--expired' : 'badge badge--revoked'
                              }>
                                {file.status === 'active' ? <><span className="material-symbols-outlined icon-sm">check</span> アクティブ</> :
                                 file.status === 'expired' ? '期限切れ' : '無効'}
                              </span>
                            </td>

                            {/* Actions */}
                            <td data-label="アクション">
                              <div style={{ display: 'flex', gap: '8px' }}>
                                {!isInactive && (
                                  <button
                                    onClick={() => handleDownload(file.shareId, file.title)}
                                    disabled={downloadingShareId === file.shareId}
                                    className="btn btn--sm btn--outline-cyan"
                                  >
                                    {downloadingShareId === file.shareId ? 'ダウンロード中...' : 'ダウンロード'}
                                  </button>
                                )}
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>

                {/* Pagination Controls */}
                {files.length > 0 && (
                  <div className="pagination">
                    <div className="pagination__info">
                      {pagination.count}件の結果を表示中
                    </div>
                    <div className="pagination__buttons">
                      <button
                        onClick={handlePrevPage}
                        disabled={cursorHistory.length === 0}
                        className="btn btn--secondary"
                      >
                        ← 前へ
                      </button>
                      <button
                        onClick={handleNextPage}
                        disabled={!pagination.hasMore}
                        className="btn btn--secondary"
                      >
                        次へ →
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </>
          )}
        </div>
      </div>

      <MessageDialog {...dialogState} onClose={closeDialog} />
    </>
  );
};

export default ReceivedFiles;
